import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { EXPERIENCE_VARIABLE_NAME_MAX } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * « Nom de variable », partagé par l'Input, la Checkbox et le Bouton qui retient
 * son choix (refonte modulaire §8.6, §9.2; planche `Inspecteurs`; issue #58).
 *
 * §8.6 is the whole of this field in two sentences: « le nom de variable qu'on
 * saisit dans l'inspecteur n'est QU'UN LIBELLÉ : ni une clé, ni une adresse »,
 * and it « doit être unique dans la timeline, vérifié au geste Enregistrer une
 * nouvelle version ». So the field says both - what it is for, and that a
 * rename costs nothing - and it names the duplicate.
 *
 * ## Le doublon se dit à la FRAPPE, et se refuse à l'enregistrement
 *
 * The server answers `duplicate-variable-name` at the save and the dry run of
 * the box words it (§9.1). That is the authority, and it is also the moment
 * somebody has stopped typing. Saying it here as well costs one pure function
 * (`duplicateVariableNames`) and turns a refusal into a correction: the two
 * count the same thing - `namedVariablesOf` - so they cannot disagree.
 */

export interface VariableNameFieldProps {
  readonly value: string;
  readonly readOnly: boolean;
  /** The name is declared twice or more in the timeline (§8.6). */
  readonly duplicate: boolean;
  readonly onChange: (next: string) => void;
}

export function VariableNameField({
  value,
  readOnly,
  duplicate,
  onChange,
}: VariableNameFieldProps) {
  const fieldId = useId();
  const hintId = useId();

  return (
    <div className="space-y-1.5">
      <label htmlFor={fieldId} className="block font-semibold text-gray-800 text-xs">
        Nom de variable
      </label>
      <input
        id={fieldId}
        data-testid={PAGE_EDITOR_IDS.blockVariable}
        value={value}
        readOnly={readOnly}
        maxLength={EXPERIENCE_VARIABLE_NAME_MAX}
        aria-invalid={duplicate}
        aria-describedby={hintId}
        onChange={(event) => onChange(event.currentTarget.value)}
        className={`h-9 w-full rounded-md border-2 bg-white px-3 text-sm read-only:bg-gray-50 read-only:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
          duplicate ? 'border-rouge' : 'border-gray-300 focus-visible:border-bleu-nuit'
        }`}
      />
      {duplicate ? (
        <p
          id={hintId}
          role="alert"
          data-testid={PAGE_EDITOR_IDS.variableDuplicate}
          className="text-rouge-deep text-xs leading-4"
        >
          Une autre variable de la timeline porte déjà « {value} ». L’enregistrement d’une version
          sera refusé tant que les deux noms sont les mêmes.
        </p>
      ) : (
        <p id={hintId} className="text-gray-600 text-xs leading-4">
          Un libellé, unique dans la timeline ; le jeton stocké porte l’identifiant du bloc, donc
          renommer ne casse rien.
          {value === '' ? null : ` Insérable ailleurs par @${value}.`}
        </p>
      )}
    </div>
  );
}
